// LayoutWithSidebar.jsx - Professional Version
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import cn from 'classnames';
import { useTranslation } from "react-i18next";
import { getTabsByPrivilege } from "../../constants/common";
import './LayoutWithSidebar.styles.scss';

/**
 * Professional LayoutWithSidebar component with enhanced UX and accessibility.
 * Features modern design, smooth animations, and responsive behavior.
 */
const LayoutWithSidebar = ({
  children,
  className,
  onTabClick = () => {},
  tab: selectedTab,
  brandTitle = "Dashboard", // Optional brand/app title
  showBrand = false, // Whether to show brand section
  collapsible = false, // Future feature for collapsible sidebar
}) => {
  const { t } = useTranslation();
  const tabs = getTabsByPrivilege();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Handle responsive behavior
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Handle keyboard navigation
  const handleKeyDown = (event, tabValue) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (selectedTab !== tabValue) {
        onTabClick(tabValue);
      }
    }
  };

  // Handle tab click with analytics/tracking support
  const handleTabClick = (tabValue) => {
    if (selectedTab !== tabValue) {
      // Optional: Add analytics tracking here
      // analytics.track('sidebar_navigation', { from: selectedTab, to: tabValue });
      onTabClick(tabValue);
    }
  };

  // Get icon component or string
  const renderIcon = (icon) => {
    if (typeof icon === 'string') {
      return <i className={icon} />;
    }
    return icon;
  };

  return (
    <div className={cn('sidebar-layout', className, {
      'sidebar-layout--collapsed': isCollapsed,
      'sidebar-layout--mobile': isMobile
    })}>
      <nav 
        className="sidebar-layout__navigation"
        role="navigation"
        aria-label="Main navigation"
      >
        {/* Optional Brand/Logo Section */}
        {showBrand && (
          <div className="sidebar-layout__brand">
            <h2>{brandTitle}</h2>
          </div>
        )}

        {/* Navigation Menu */}
        <ul className="sidebar-layout__menu" role="menubar">
          {tabs.map((tab, index) => {
            const isActive = selectedTab === tab.value;
            const isDisabled = tab.disabled;
            
            return (
              <li
                key={`tab-${tab.value}`}
                className={cn(
                  "sidebar-layout__menu-item",
                  {
                    "sidebar-layout__menu-item--active": isActive,
                    "sidebar-layout__menu-item--disabled": isDisabled
                  }
                )}
                onClick={() => !isDisabled && handleTabClick(tab.value)}
                onKeyDown={(e) => !isDisabled && handleKeyDown(e, tab.value)}
                role="menuitem"
                tabIndex={isDisabled ? -1 : 0}
                aria-selected={isActive}
                aria-disabled={isDisabled}
                aria-current={isActive ? 'page' : undefined}
                title={t(tab.description || tab.label)} // Tooltip support
              >
                {/* Icon */}
                {tab.icon && (
                  <span 
                    className="sidebar-layout__icon"
                    aria-hidden="true"
                  >
                    {renderIcon(tab.icon)}
                  </span>
                )}

                {/* Label */}
                <span className="sidebar-layout__label">
                  {t(tab.label)}
                </span>

                {/* Optional Badge/Notification Count */}
                {tab.badge && (
                  <span 
                    className="sidebar-layout__badge"
                    aria-label={`${tab.badge} notifications`}
                  >
                    {tab.badge}
                  </span>
                )}
              </li>
            );
          })}
        </ul>

        {/* Optional Collapse Toggle (for future use) */}
        {collapsible && !isMobile && (
          <button
            className="sidebar-layout__toggle"
            onClick={() => setIsCollapsed(!isCollapsed)}
            aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            aria-expanded={!isCollapsed}
          >
            <span className="sidebar-layout__toggle-icon">
              {isCollapsed ? '→' : '←'}
            </span>
          </button>
        )}
      </nav>

      {/* Main Content Area */}
      <main 
        className="sidebar-layout__content"
        role="main"
        aria-label="Main content"
      >
        {children}
      </main>
    </div>
  );
};

LayoutWithSidebar.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  onTabClick: PropTypes.func,
  tab: PropTypes.string,
  brandTitle: PropTypes.string,
  showBrand: PropTypes.bool,
  collapsible: PropTypes.bool,
};

export default React.memo(LayoutWithSidebar);